import { useLayoutEffect } from 'react'
import { NAVIGATION } from './navigation'

/**
 * **Jede Seite bekommt einen eigenen Titel** (17.09.2026,
 * [E-365](../../../docs/ENTSCHEIDUNGEN.md#e-365)).
 *
 * Nils: *„Work meldet dort aktuell für jede Seite nur `NetCon Work`. In der Liste stehen dadurch
 * mehrere gleich aussehende Einträge, und man erkennt nicht, wohin sie führen."*
 *
 * ## Warum das in einer SPA nicht von selbst passiert
 *
 * Der Titel steht **einmal** in `index.html` und bleibt dort stehen, solange niemand ihn ändert.
 * Ein Routenwechsel tauscht in React nur Komponenten aus – das Dokument bleibt dasselbe, und mit
 * ihm sein `<title>`. Bei einer Anwendung aus mehreren HTML-Seiten wäre nichts zu tun.
 *
 * ## Das wirkt an drei Stellen gleichzeitig
 *
 * Im Browser-Tab, im Verlauf des Browsers – und in *„Zuletzt besucht"* auf der Startseite von
 * NetCon.Cloud. Die Kopfleiste meldet dem Portal Adresse und Titel der Seite, auf der sie liegt.
 *
 * **Und das Portal fasst zusammen, was gleich heißt.** Mehrere Seiten unter demselben Titel
 * erscheinen also als *ein* Eintrag, nämlich der zuletzt besuchte. Sprechende Titel sind damit
 * nicht nur schöner, sondern die Bedingung dafür, dass die Liste überhaupt vollständig ist.
 *
 * ## Wann gemeldet wird – nachgemessen, nicht vermutet
 *
 * Im Skript der Leiste (`https://netcon.cloud/embed/header.v1.js`) nachgelesen: Sie hängt einen
 * `MutationObserver` an das `<title>`-Element, horcht auf `popstate` und wickelt `pushState` ein.
 * Jedes dieser Ereignisse stößt `spaeter()` an – **800 ms Entprellung**, dann geht
 * `{ app, url, title }` an `/api/v1/visits`.
 *
 * Für uns heißt das zweierlei. Erstens: Der Titel muss beim Rendern stehen, nicht irgendwann –
 * deshalb `useLayoutEffect` und nicht `useEffect`. Zweitens: Die 800 ms sind der Sicherheitsgurt,
 * nicht der Plan. Wer sich darauf verlässt, baut eine Anwendung, die bei einer schnelleren
 * Fassung der Leiste stumm falsche Titel meldet.
 */

/** **Hinten, immer.** Das Konkrete zuerst – so steht es im Tab auch dann, wenn er schmal ist. */
export const ANWENDUNG = 'NetCon Work'

/** Der Trenner ist ein Geviertstrich mit Leerzeichen, wie bei NetCon Project. */
const TRENNER = ' — '

/**
 * **Aus dem Seitennamen wird der Dokumenttitel.**
 *
 * Ohne Namen bleibt es beim bloßen `NetCon Work` – dasselbe wie in `index.html`. Das ist der
 * richtige Rückfall: Ein Titel wie `„ — NetCon Work"` sähe nach einem Fehler aus, und ein
 * erfundener Name wäre schlimmer als keiner.
 */
export function titelFuer(seite: string | null | undefined): string {
  const name = seite?.trim() ?? ''
  return name === '' ? ANWENDUNG : `${name}${TRENNER}${ANWENDUNG}`
}

/**
 * **Der Name der Ansicht zu einem Pfad** – aus `NAVIGATION`, nicht aus einer zweiten Liste.
 *
 * Die Seitenleiste, die ⌘K-Palette und der Router teilen sich diese Quelle schon; ein eigenes
 * Verzeichnis für Titel wäre die vierte Stelle, an der ein Name stehen kann, und die erste, die
 * beim nächsten Umbenennen vergessen wird.
 *
 * Pfade außerhalb der Navigation (etwa der Rückweg von NetCon.Cloud) haben bewusst keinen Namen:
 * Sie sind keine Ansichten, und sie sollen im Verlauf auch nicht auftauchen.
 */
export function ansichtsname(pfad: string): string | null {
  return NAVIGATION.find((eintrag) => eintrag.path === pfad)?.label ?? null
}

/**
 * **Den Titel setzen, solange diese Komponente steht.**
 *
 * `useLayoutEffect` und nicht `useEffect`: Der Titel gehört zum Rendern, nicht zu dem, was danach
 * noch nachgereicht wird. Nils ausdrücklich: *„beim Routenwechsel also zusammen mit dem Rendern,
 * nicht verzögert."*
 *
 * Aufgeräumt wird **nicht**. Der Titel gehört dem Dokument, nicht der Komponente – ihn beim
 * Aushängen auf den Rückfall zurückzusetzen hieße, dass zwischen zwei Ansichten für einen
 * Wimpernschlag `NetCon Work` stünde. Die nächste Ansicht setzt ihn ohnehin, und beim Verlassen
 * der Anwendung ist er gleichgültig.
 */
export function useSeitentitel(seite: string | null | undefined): void {
  useLayoutEffect(() => {
    document.title = titelFuer(seite)
  }, [seite])
}
